import { Injectable } from '@angular/core';
import { Announcement, IAnnouncement } from '../models/announcement.model';
import { AuthService } from './auth.service';
import { HttpClient } from '@angular/common/http';
import { SpinnerService } from './spinner.service';
import { ToastService } from './toast.service';
import { environment } from '../../environments/environment';
import { IServerResponse, ServerResponse } from '../models/server-response.model';
import { IPresignResponse } from '../models/presign-response.model';
import { AwsService } from './aws.service';
import { IUploadPosition } from '../models/upload-progress.model';

@Injectable({
  providedIn: 'root'
})
export class AnnouncementService {

  public loading: boolean = false;
  public announcements: Announcement[] = [];

  constructor(
    public authService: AuthService,
    public awsService: AwsService,
    private http: HttpClient,
    public spinnerService: SpinnerService,
    public toastService: ToastService
  ) { }

  /*
   ██████╗██████╗ ███████╗ █████╗ ████████╗███████╗
  ██╔════╝██╔══██╗██╔════╝██╔══██╗╚══██╔══╝██╔════╝
  ██║     ██████╔╝█████╗  ███████║   ██║   █████╗
  ██║     ██╔══██╗██╔══╝  ██╔══██║   ██║   ██╔══╝
  ╚██████╗██║  ██║███████╗██║  ██║   ██║   ███████╗
   ╚═════╝╚═╝  ╚═╝╚══════╝╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
  async create(
    neighborhoodId: string,
    announcement: IAnnouncement
  ): Promise<Announcement | null> {
    let announcementCreated: Announcement | null = null;
    
    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/announcements`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Registrando anuncio...';

    try {
      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse<{announcement: IAnnouncement}>>(
        (resolve, reject) => {
          this.http.post<IServerResponse<{announcement: IAnnouncement}>>(
            urlApi,
            JSON.stringify(announcement),
            {
              headers: {
                'content-type': 'application/json',
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (response: IServerResponse) => resolve(
              new ServerResponse(response)
            ),
            error: (error) => {
              if (error.error) resolve(new ServerResponse(error.error));
              else reject(error);
            },
          });
        }
      );

      if(response.statusCode === 201) {
        
        announcementCreated = new Announcement(response.data!.announcement);

        this.toastService.show({ 
          body: `${response.message}`, 
          classname: 'bg-success text-white' 
        });
        this.getAll(
          neighborhoodId
        );

      } else {
        
        this.toastService.show({ 
          header: 'Error', 
          body: `${response.message}`, 
          classname: 'bg-danger text-white' 
        });

      }

    } catch(error) {
      this.toastService.show({ 
        header: 'Error', 
        body: `${error}`, 
        classname: 'bg-danger text-white' 
      });
    }

    this.spinnerService.loading = false;
    return announcementCreated;
  }

  /*
  ██████╗ ███████╗██╗     ███████╗████████╗███████╗
  ██╔══██╗██╔════╝██║     ██╔════╝╚══██╔══╝██╔════╝
  ██║  ██║█████╗  ██║     █████╗     ██║   █████╗
  ██║  ██║██╔══╝  ██║     ██╔══╝     ██║   ██╔══╝
  ██████╔╝███████╗███████╗███████╗   ██║   ███████╗
  ╚═════╝ ╚══════╝╚══════╝╚══════╝   ╚═╝   ╚══════╝

  */
  async delete(
    announcement: Announcement
  ): Promise<boolean> {
    let flag = false;
    const urlApi = `${environment.urlApi}/neighborhoods/${announcement.neighborhoodId}/announcements/${announcement.announcementId}`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Eliminando anuncio...';

    try {
      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.delete<IServerResponse>(
            urlApi,
            { 
              headers: { 
                'Authorization': token.toString() 
              } 
            }
          ).subscribe({
            next: (response: IServerResponse) => resolve(
              new ServerResponse(response)
            ),
            error: (error) => {
              if (error.error) resolve(new ServerResponse(error.error));
              else reject(error);
            },
          });
        }
      );

      if(response.statusCode === 200) {
        flag = true;

        this.toastService.show({ 
          body: `${response.message}`, 
          classname: 'bg-success text-white' 
        });

        this.getAll(
          announcement.neighborhoodId
        );

      } else {

        this.toastService.show({ 
          header: 'Error', 
          body: `${response.message}`, 
          classname: 'bg-danger text-white' 
        });

      }

    } catch(error) {

      this.toastService.show({ 
        header: 'Error', 
        body: `${error}`, 
        classname: 'bg-danger text-white' 
      });

    }

    this.spinnerService.loading = false;
    return flag;
  }

  /*
   ██████╗ ███████╗████████╗ █████╗ ██╗     ██╗
  ██╔════╝ ██╔════╝╚══██╔══╝██╔══██╗██║     ██║
  ██║  ███╗█████╗     ██║   ███████║██║     ██║
  ██║   ██║██╔══╝     ██║   ██╔══██║██║     ██║
  ╚██████╔╝███████╗   ██║   ██║  ██║███████╗███████╗
   ╚═════╝ ╚══════╝   ╚═╝   ╚═╝  ╚═╝╚══════╝╚══════╝

  */
  async getAll(
    neighborhoodId: string
  ) {
    this.loading = true;
    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/announcements`;

    try {
      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse<{ announcements: IAnnouncement[] }>>(
        (resolve, reject) => {
          this.http.get<IServerResponse<{ announcements: IAnnouncement[] }>>(
            urlApi,
            { 
              headers: { 
                "content-type": "application/json", 
                "Authorization": token.toString() 
              } 
            }
          ).subscribe({
            next: (response) => resolve(new ServerResponse(response)),
            error: (error) => reject(error),
          });
        }
      );

      console.log(response);

      if (response.statusCode === 200) {
        this.announcements = response.data!.announcements.map(
          ann => new Announcement(ann)
        );
      }

    } catch(error) {

      this.toastService.show({ 
        header: 'Error', 
        body: `${error}`, 
        classname: 'bg-danger text-white' 
      });

    }

    this.loading = false;
  }

  /*
  ██╗   ██╗██████╗ ██████╗  █████╗ ████████╗███████╗
  ██║   ██║██╔══██╗██╔══██╗██╔══██╗╚══██╔══╝██╔════╝
  ██║   ██║██████╔╝██║  ██║███████║   ██║   █████╗
  ██║   ██║██╔═══╝ ██║  ██║██╔══██║   ██║   ██╔══╝
  ╚██████╔╝██║     ██████╔╝██║  ██║   ██║   ███████╗
   ╚═════╝ ╚═╝     ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
  async update(
    announcement: IAnnouncement
  ): Promise<boolean> {
    let flag = false;
    const urlApi = `${environment.urlApi}/neighborhoods/${announcement.neighborhoodId}/announcements/${announcement.announcementId}`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Actualizando anuncio...';

    try {
      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.put<IServerResponse>(
            urlApi,
            JSON.stringify(announcement),
            {
              headers: {
                'content-type': 'application/json',
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (response: IServerResponse) => resolve(
              new ServerResponse(response)
            ),
            error: (error) => {
              if (error.error) resolve(new ServerResponse(error.error));
              else reject(error);
            },
          });
        }
      );

      if (response.statusCode === 200) {

        flag = true;
        this.toastService.show({ 
          body: `${response.message}`, 
          classname: 'bg-success text-white' 
        });
        this.getAll(
          announcement.neighborhoodId
        );

      } else {
        this.toastService.show({ 
          header: 'Error', 
          body: `${response.message}`, 
          classname: 'bg-danger text-white' 
        });
      }

    } catch (error) {
      this.toastService.show({ 
        header: 'Error', 
        body: `${error}`, 
        classname: 'bg-danger text-white' 
      });
    }

    this.spinnerService.loading = false;
    return flag;
  }

  /*
  ██╗   ██╗██████╗ ██╗      ██████╗  █████╗ ██████╗ ███████╗██╗██╗     ███████╗
  ██║   ██║██╔══██╗██║     ██╔═══██╗██╔══██╗██╔══██╗██╔════╝██║██║     ██╔════╝
  ██║   ██║██████╔╝██║     ██║   ██║███████║██║  ██║█████╗  ██║██║     █████╗
  ██║   ██║██╔═══╝ ██║     ██║   ██║██╔══██║██║  ██║██╔══╝  ██║██║     ██╔══╝
  ╚██████╔╝██║     ███████╗╚██████╔╝██║  ██║██████╔╝██║     ██║███████╗███████╗
   ╚═════╝ ╚═╝     ╚══════╝ ╚═════╝ ╚═╝  ╚═╝╚═════╝ ╚═╝     ╚═╝╚══════╝╚══════╝

  */
  async uploadFile(
    announcement: Announcement,
    file: File | string,
    fileName?: string,
    position: IUploadPosition | null = null
  ): Promise<boolean> {

    let flag = false;

    const name = fileName ?? (
      typeof file === 'string' ? 'Imagen' : file.name
    );

    let uploadBlob: Blob;
    let contentType: string;

    if (typeof file === 'string') {
      const match = file.match(/^data:(.+?);base64,/);
      contentType = match ? match[1] : 'application/octet-stream';
      const byteString = atob(file.split(',')[1]);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      uploadBlob = new Blob([ab], { type: contentType });
    } else {
      uploadBlob = file;
      contentType = file.type;
    }

    const urlApi = `${environment.urlApi}/neighborhoods/${announcement.neighborhoodId}/announcements/${announcement.announcementId}/files`;

    this.spinnerService.loading = true;
    this.spinnerService.startUpload(name, position);

    try {
      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse<{ presignFile: IPresignResponse }>>(
        (resolve, reject) => {
          this.http.post<IServerResponse<{ presignFile: IPresignResponse }>>(
            urlApi,
            JSON.stringify({ contentType }),
            {
              headers: {
                'content-type': 'application/json',
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (response: IServerResponse<{ presignFile: IPresignResponse }>) => resolve(
              new ServerResponse<{ presignFile: IPresignResponse }>(response)
            ),
            error: (error) => {
              if (error.error) resolve(new ServerResponse(error.error));
              else reject(error);
            },
          });
        }
      );

      if (response.statusCode === 201) {

        flag = true;

        if (response.data?.presignFile?.uploadUrl) {
          await this.awsService.uploadFile(
            response.data.presignFile.uploadUrl,
            uploadBlob,
            contentType,
            name,
            position
          );
        }

        this.toastService.show({
          body: `${response.message}`,
          classname: 'bg-success text-white'
        });

        this.getAll(
          announcement.neighborhoodId
        );

      } else {
        this.toastService.show({
          header: 'Error',
          body: `${response.message}`,
          classname: 'bg-danger text-white'
        });
      }

    } catch (error) {
      console.error(error);
      this.toastService.show({
        header: 'Error',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';
    this.spinnerService.clearUpload();

    return flag;

  }
  
}
